<script setup lang="ts">
import { computed, onMounted, ref, watch, watchEffect } from "vue";
import AnimalIcon from "./emoji_groups/AnimalIcon.vue";
import BallIcon from "./emoji_groups/BallIcon.vue";
import CarIcon from "./emoji_groups/CarIcon.vue";
import EmojiGroupItem from "./emoji_groups/EmojiGroupItem.vue";
import EmojiItem from "./emoji_groups/EmojiItem.vue";
import FacesIcon from "./emoji_groups/FacesIcon.vue";
import FruitIcon from "./emoji_groups/FruitIcon.vue";
import LightIcon from "./emoji_groups/LightIcon.vue";
import RecentIcon from "./emoji_groups/RecentIcon.vue";
import SimpleBar from "simplebar";
import {
  activities_emoji,
  animals_emoji,
  faces_emoji,
  // flags_emoji,
  fruits_emoji,
  objects_emoji,
  travel_emoji,
} from "./emoji_groups/emoji_list";

const emojiScroll = ref<HTMLElement | null>(null);
const selectedGroup = ref<keyof typeof emojiContent | "recent">("recent");
let scrollEl: SimpleBar;

watchEffect(() => {
  if (emojiScroll.value) {
    scrollEl = new SimpleBar(emojiScroll.value);
  }
});

const emojiContent = {
  faces: faces_emoji,
  animals: animals_emoji,
  fruits: fruits_emoji,
  activities: activities_emoji,
  travel: travel_emoji,
  obj: objects_emoji,
  // flags: flags_emoji,
};

const emojiGroupContent = computed(() => {
  if (selectedGroup.value === "recent") {
    return getRecent();
  } else return emojiContent[selectedGroup.value];
});

function getRecent() {
  const recent = localStorage.getItem('recent-emoji')
  if(recent) {
    const emoji = JSON.parse(recent) as string[]
    return emoji
  } else return []
}

function pushRecent(s: string) {
  const recent = getRecent()
  recent.unshift(s)
  localStorage.setItem('recent-emoji', JSON.stringify(Array.from(new Set(recent))))
}

onMounted(() => {
  if (
    emojiGroupContent.value.length === 0 &&
    selectedGroup.value === "recent"
  ) {
    selectedGroup.value = "faces";
  }
});

watch(
  () => [emojiGroupContent.value, selectedGroup.value],
  () => {
    const scroll = scrollEl?.getScrollElement();
    if (scroll) {
      scroll.scrollTop = 0;
    }
  },
  { deep: true, immediate: true },
);

const emits = defineEmits<{
  (e: "pick", data: string): void;
}>();

const pick = (emoji: string) => {
  emits("pick", emoji);
  pushRecent(emoji)
};
</script>
<template>
  <div class="emoji-picker">
    <div class="emoji-groups">
      <EmojiGroupItem :active="selectedGroup === 'recent'" @click="selectedGroup = 'recent'">
        <RecentIcon />
      </EmojiGroupItem>
      <EmojiGroupItem :active="selectedGroup === 'faces'" @click="selectedGroup = 'faces'">
        <FacesIcon />
      </EmojiGroupItem>
      <EmojiGroupItem :active="selectedGroup === 'animals'" @click="selectedGroup = 'animals'">
        <AnimalIcon />
      </EmojiGroupItem>
      <EmojiGroupItem :active="selectedGroup === 'fruits'" @click="selectedGroup = 'fruits'">
        <FruitIcon />
      </EmojiGroupItem>
      <EmojiGroupItem :active="selectedGroup === 'activities'" @click="selectedGroup = 'activities'">
        <BallIcon />
      </EmojiGroupItem>
      <EmojiGroupItem :active="selectedGroup === 'travel'" @click="selectedGroup = 'travel'">
        <CarIcon />
      </EmojiGroupItem>
      <EmojiGroupItem :active="selectedGroup === 'obj'" @click="selectedGroup = 'obj'">
        <LightIcon />
      </EmojiGroupItem>
    </div>
    <div ref="emojiScroll" class="emoji-list">
      <div class="emoji-inner-list">
        <EmojiItem v-for="(e, id) in emojiGroupContent" :content="e" :key="`face=${id}`" @click="pick(e)" />
      </div>
    </div>
  </div>
</template>
<style scoped>
.emoji-picker {
  width: 100%;
  height: 220px;
  display: flex;
  flex-direction: column;
  user-select: none;
}

.emoji-groups {
  height: 44px;
  width: 100%;
  flex-shrink: 0;
  display: flex;
  justify-content: space-between;
  padding: 0px 4px;
  background-color: #3e3b3b;
}

.emoji-list {
  width: 100%;
  height: 176px;
  flex-grow: 1;
  background-color: #332f2f;
}

.emoji-inner-list {
  width: 100%;
  /* min-height: 176px; */
  display: flex;
  flex-wrap: wrap;
  padding: 4px 4px;
  background-color: #332f2f;
}
</style>
<style>
.emoji-list .simplebar-vertical .simplebar-scrollbar::before {
  background-color: #676666;
  width: 4px;
}
</style>
